/**
 * Prompt Area Height Utilities
 *
 * Manages the prompt input area height with localStorage persistence.
 * Mirrors the pattern in panelWidth.ts for the artefact resize handle.
 */

import { logger } from "@/telemetry";

export const PROMPT_HEIGHT_KEY = "promptAreaHeight";
export const MIN_PROMPT_HEIGHT = 120; // px – enough for textarea + action buttons
export const MAX_PROMPT_HEIGHT_RATIO = 0.6; // 60% of container height

/**
 * Get the initial prompt area height from localStorage.
 * Returns null if no value is saved (meaning "auto" / natural sizing).
 */
export function getInitialPromptHeight(): number | null {
  try {
    const saved = localStorage.getItem(PROMPT_HEIGHT_KEY);

    if (saved) {
      const height = parseFloat(saved);

      if (height >= MIN_PROMPT_HEIGHT) {
        return height;
      }
    }
  } catch (error) {
    logger.warn("Failed to load saved prompt height:", { error: String(error) });
  }

  return null;
}

/**
 * Save the prompt area height to localStorage.
 * Pass null to remove the saved value (restore auto sizing).
 */
export function savePromptHeight(height: number | null): void {
  try {
    if (height == null) {
      localStorage.removeItem(PROMPT_HEIGHT_KEY);
    } else {
      localStorage.setItem(PROMPT_HEIGHT_KEY, height.toString());
    }
  } catch (error) {
    logger.warn("Failed to save prompt height:", { error: String(error) });
  }
}

/**
 * Constrain a height value to the valid range.
 */
export function constrainPromptHeight(height: number, containerHeight: number): number {
  const maxHeight = containerHeight * MAX_PROMPT_HEIGHT_RATIO;
  // Ensure min never exceeds max on very small viewports
  const effectiveMin = Math.min(MIN_PROMPT_HEIGHT, maxHeight);

  return Math.max(effectiveMin, Math.min(maxHeight, height));
}
